import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ManageViewsDialog } from "_shared/_views/list-pages/manage-views-dialog";
import {
  useGetTableColumnPreferencesQuery,
  useResetAllTableColumnVisibilityMutation,
  useResetTableColumnVisibilityMutation,
} from "_shared/_views/tables/_api/table-column-preferences-api";
import type { TableColumnPreferenceJSON } from "_shared/_views/tables/_schemas/table-column-preferences-schema";
import {
  hasAnyColumnVisibility,
  removeAllColumnVisibility,
  removeColumnVisibility,
} from "_shared/_views/tables/table-column-visibility-preference";
import { tableTitle } from "_shared/_views/tables/table-titles";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useState } from "react";
import { useSession } from "users/__session/session-context";
import SettingsSection from "users/settings/_views/settings-section";

export const ALL_TABLES_RESET_MESSAGE = "Every table shows its default columns again.";
export const ALL_TABLES_RESET_FAILED_MESSAGE = "Could not reset the columns of your tables. Try again.";

export function tableResetMessage(title: string): string {
  return `${title} shows its default columns again.`;
}

export function tableResetFailedMessage(title: string): string {
  return `Could not reset the columns of ${title}. Try again.`;
}

export const RESET_ALL_TABLES_QUESTION =
  "Reset every table? Each list opens in its starred view, or its Default view, with its default columns. Your saved views stay.";

export function hiddenColumnsText(table: Pick<TableColumnPreferenceJSON, "columns">): string {
  const hidden = table.columns.filter((column) => !column.visible).length;
  if (hidden === 0) return "No columns hidden";
  return hidden === 1 ? "1 column hidden" : `${hidden} columns hidden`;
}

/** The saved views of a table, and the one a visit from the side menu opens (#6717). */
export function savedViewsText(table: Pick<TableColumnPreferenceJSON, "views">): string {
  const views = table.views ?? [];
  if (views.length === 0) return "No saved views";
  const count = views.length === 1 ? "1 saved view" : `${views.length} saved views`;
  const starred = views.find((view) => view.isStarred);
  return starred ? `${count} · opens with ${starred.name}` : count;
}

type SavedTable = TableColumnPreferenceJSON & { title: string };

/** A table with saved views and no current view has nothing a reset would change (#6714). */
function isResettable(table: TableColumnPreferenceJSON): boolean {
  return table.currentView !== null || table.views === undefined;
}

/**
 * The columns a person chose in each table's Show/Hide columns menu, and a way back to each table's
 * defaults (#6151).
 *
 * The account keeps each choice (#2788), so a reset here goes to the server first. This browser's
 * copy goes only after the server has forgotten the choice: the table then opens with its default
 * columns here before the server answers, and in every other browser once it does.
 */
export default function TableSettingsSection() {
  const session = useSession();
  const query = useGetTableColumnPreferencesQuery({ session }, { skip: !session?.isLoggedIn });
  const [resetTable, resetTableState] = useResetTableColumnVisibilityMutation();
  const [resetAllTables, resetAllState] = useResetAllTableColumnVisibilityMutation();
  const resetting = resetTableState.isLoading || resetAllState.isLoading;
  // A copy the account never received, from a save that failed. Only Reset all tables forgets it.
  const [browserKeepsChoices, setBrowserKeepsChoices] = useState(() => hasAnyColumnVisibility());
  const [confirmingResetAll, setConfirmingResetAll] = useState(false);
  const [managingKey, setManagingKey] = useState<string | undefined>();

  const tables: SavedTable[] = (query.data?.tables ?? [])
    .map((table) => ({ ...table, title: tableTitle(table.tableKey) }))
    .sort((left, right) => left.title.localeCompare(right.title));
  const managing = tables.find((table) => table.tableKey === managingKey);

  const reset = async (table: SavedTable) => {
    try {
      await resetTable({ session, tableKey: table.tableKey }).unwrap();
    } catch {
      enqueueSnackbar(tableResetFailedMessage(table.title), { variant: "error" });
      return;
    }
    removeColumnVisibility(table.tableKey);
    setBrowserKeepsChoices(hasAnyColumnVisibility());
    enqueueSnackbar(tableResetMessage(table.title), { variant: "success" });
  };

  const resetAll = async () => {
    setConfirmingResetAll(false);
    try {
      await resetAllTables({ session }).unwrap();
    } catch {
      enqueueSnackbar(ALL_TABLES_RESET_FAILED_MESSAGE, { variant: "error" });
      return;
    }
    removeAllColumnVisibility();
    setBrowserKeepsChoices(false);
    enqueueSnackbar(ALL_TABLES_RESET_MESSAGE, { variant: "success" });
  };

  return (
    <SettingsSection
      description="Your saved views and unsaved edits for each table, with the columns you chose in Show/Hide columns. Your account keeps them, so every browser you sign in to shows the same lists."
      title="Tables"
    >
      <TableSettingsStatus
        browserKeepsChoices={browserKeepsChoices}
        isError={query.isError}
        isLoading={query.isLoading}
        tableCount={tables.length}
      />
      {tables.length > 0 && (
        <List aria-label="Tables with saved views or columns" disablePadding>
          {tables.map((table) => (
            <ListItem
              disableGutters
              divider
              key={table.tableKey}
              sx={{
                alignItems: { sm: "center", xs: "flex-start" },
                flexDirection: { sm: "row", xs: "column" },
                gap: 1,
              }}
            >
              <ListItemText primary={table.title} secondary={`${hiddenColumnsText(table)} · ${savedViewsText(table)}`} />
              <Stack direction="row" sx={{ flexShrink: 0, flexWrap: "wrap", gap: 1 }}>
                <Button
                  aria-label={`Manage views for ${table.title}`}
                  disabled={(table.views ?? []).length === 0}
                  onClick={() => setManagingKey(table.tableKey)}
                  size="small"
                >
                  Manage views
                </Button>
                <Button
                  aria-label={`Reset to default columns for ${table.title}`}
                  disabled={resetting || !isResettable(table)}
                  onClick={() => void reset(table)}
                  size="small"
                  variant="outlined"
                >
                  Reset to default columns
                </Button>
              </Stack>
            </ListItem>
          ))}
        </List>
      )}
      {confirmingResetAll ? (
        <Alert severity="warning">
          {RESET_ALL_TABLES_QUESTION}
          {/* Under the sentence rather than in the Alert's action slot, where the narrow column
              broke "Yes, reset all tables" over four lines. */}
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1, mt: 1.5 }}>
            <Button color="inherit" disabled={resetting} onClick={() => void resetAll()} size="small" variant="outlined">
              Yes, reset all tables
            </Button>
            <Button color="inherit" onClick={() => setConfirmingResetAll(false)} size="small">
              Cancel
            </Button>
          </Stack>
        </Alert>
      ) : (
        <div>
          <Button
            disabled={resetting || (!tables.some(isResettable) && !browserKeepsChoices)}
            onClick={() => setConfirmingResetAll(true)}
            variant="outlined"
          >
            Reset all tables
          </Button>
        </div>
      )}
      <ManageViewsDialog
        open={managing !== undefined}
        tableKey={managing?.tableKey ?? ""}
        title={managing?.title ?? ""}
        views={managing?.views ?? []}
        onClose={() => setManagingKey(undefined)}
      />
    </SettingsSection>
  );
}

type TableSettingsStatusProps = {
  browserKeepsChoices: boolean;
  isError: boolean;
  isLoading: boolean;
  tableCount: number;
};

function TableSettingsStatus(props: Readonly<TableSettingsStatusProps>) {
  if (props.isLoading) return <Typography color="text.secondary">Loading your saved columns.</Typography>;
  if (props.isError) {
    return (
      <Typography color="error" role="alert">
        Could not load your saved columns. Reload the page to try again.
      </Typography>
    );
  }
  if (props.tableCount === 0 && props.browserKeepsChoices) {
    return (
      <Typography>
        Your account keeps no column choices, but this browser still has some. Reset all tables to remove them.
      </Typography>
    );
  }
  if (props.tableCount === 0) return <Typography>Every table shows its default columns.</Typography>;
  return <Typography>You changed the views or columns of these tables.</Typography>;
}
